import type { Localized } from '@re/shared';

export const FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'number_range',
  'currency',
  'phone',
  'email',
  'date',
  'date_range',
  'boolean',
  'radio',
  'dropdown',
  'checkbox_group',
  'request_type',
  'property_category',
  'property_type',
  'property_features',
  'location',
  'map_point',
  'image_upload',
  'file_upload',
  'section',
  'info',
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

/** Display-only types: never hold a value, never referenced by conditions. */
export const DISPLAY_TYPES: ReadonlySet<FieldType> = new Set(['section', 'info']);

export const MAPS_TO = [
  'request_type',
  'category',
  'property_type',
  'features',
  'location',
  'coordinates',
  'price',
  'price_range',
  'area',
  'area_range',
  'building_area',
  'land_area',
  'outer_area',
  'inner_area_range',
  'outer_area_range',
  'land_area_range',
  'building_area_range',
  'rooms',
  'bathrooms',
  'floor',
  'contact_name',
  'contact_phone',
  'contact_email',
  'images',
  'notes',
] as const;
export type MapsTo = (typeof MAPS_TO)[number];

export const OPERATORS = [
  'eq',
  'neq',
  'in',
  'not_in',
  'contains',
  'contains_any',
  'contains_all',
  'not_contains',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'is_empty',
  'is_not_empty',
  'location_in',
] as const;
export type Operator = (typeof OPERATORS)[number];

export interface Condition {
  field: string;
  op: Operator;
  value?: unknown;
}

export interface RuleGroup {
  all?: Rule[];
  any?: Rule[];
  not?: Rule;
}

export type Rule = Condition | RuleGroup;

export interface FieldOption {
  value: string;
  label: Localized;
  /** The option is offered only when this rule holds (rules see EARLIER fields only, like a field's visibleWhen). */
  visibleWhen?: Rule | null;
}

export interface FieldValidation {
  min?: number;
  max?: number;
  step?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
}

export interface FieldConfig {
  options?: FieldOption[];
  optionsSource?: { kind: 'lookup'; table: string; filterBy?: string };
  validation?: FieldValidation;
  placeholder?: Localized;
  help?: Localized;
  unit?: string;
  width?: 'full' | 'half' | 'third';
  display?: string;
  /** location */
  depth?: LocationLevel;
  requiredDepth?: LocationLevel;
  /** the countries this form offers (ISO codes, e.g. ['JO']); empty or absent = every active country */
  countries?: string[];
  multiple?: boolean;
  /** uploads */
  maxFiles?: number;
  maxSizeMb?: number;
  accept?: string[];
  [k: string]: unknown;
}

export interface FormField {
  key: string;
  type: FieldType;
  section?: string | null;
  order: number;
  label: Localized;
  required: boolean;
  requiredWhen?: Rule | null;
  visibleWhen?: Rule | null;
  mapsTo?: MapsTo | null;
  config: FieldConfig;
}

export interface CompiledSchema {
  formId?: string;
  versionId?: string;
  version?: number;
  fields: FormField[];
}

export const LOCATION_LEVELS = ['country', 'governorate', 'area', 'neighborhood'] as const;
export type LocationLevel = (typeof LOCATION_LEVELS)[number];

export interface LocationValue {
  countryId: number;
  governorateId?: number | null;
  areaId?: number | null;
  neighborhoodId?: number | null;
}

/** map_point: a point on the map (WGS84, 6 decimals ≈ 10 cm) */
export interface MapPointValue {
  lat: number;
  lng: number;
}

export interface NumberRangeValue {
  min?: number | null;
  max?: number | null;
}

export interface DateRangeValue {
  from?: string | null;
  to?: string | null;
}

export type Answers = Record<string, unknown>;

export interface FieldError {
  field: string;
  rule: string;
  message: string;
  params?: Record<string, unknown>;
}

export interface EvaluationResult {
  /** visibility of every field key */
  visible: Record<string, boolean>;
  /** effective "required" for every visible field */
  required: Record<string, boolean>;
  /** normalised values of visible, non-empty fields only */
  values: Answers;
  /** keys present in the input but dropped (hidden, unknown or display-only) */
  stripped: string[];
  /** fields whose options depend on earlier answers → the option values offered right now */
  options: Record<string, string[]>;
  errors: FieldError[];
}
